import { BarsOutlined, DownloadOutlined, EyeOutlined, FileTextOutlined, FilterOutlined, ImportOutlined, InfoCircleOutlined, ScheduleFilled, ScheduleOutlined, SendOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { Badge, Button, Card, Collapse, Empty, Form, Modal, Popover, Select, Skeleton, Slider, Space, Table } from "antd";
import dayjs from "dayjs";
import { Calendar, CheckCheckIcon, Ship } from "lucide-react";
import moment from "moment";
import React, { forwardRef, useMemo, useState } from "react";
import { render } from "react-dom";
import { useNavigate } from "react-router-dom";

import LoadingSkeleton from "../../../../components/LoadingSkeleton.jsx";
import ScheduleModal from "../../../../components/modals/scheduleModal";
import useScheduleModal from "../../../../hooks/useScheduleModal";
import { useProposalStore } from "../../../../store/proposalStore";
import useTaxStore from "../../../../store/taxStore";
import { categorizeTax, getTaxDescription } from "../../../../utills/tax-categorizer";
import { exportToExcel, exportToPDF } from "./FreightList.utils";
import { parseTaxValue } from "./utils";

const { Panel } = Collapse;

const FreightList = forwardRef(({ data = [], loading }, ref) => {
  const navigate = useNavigate();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [containerFilter, setContainerFilter] = useState([]);
  const [carrierFilter, setCarrierFilter] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [isTaxModalVisible, setIsTaxModalVisible] = useState(false);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState([]);
  const [isDDModalVisible, setIsDDModalVisible] = useState(false);
  const [selectedDDData, setSelectedDDData] = useState(null);

  const {
    isOpen,
    dataModal,
    loadingModal,
    error,
    params,
    openModal,
    closeModal,
  } = useScheduleModal();
  const { setFees } = useProposalStore();
  const calculateTotal = (item) => {
    if (!item) return 0;
    const parse = (value) => {
      if (!value) return 0;
      if (typeof value === "number") return value;
      return parseFloat(String(value).replace(/[^\d.]/g, "")) || 0;
    };
    return parse(item.base_freight) + parse(item.bunker) + parse(item.isps);
  };

  const uniqueData = useMemo(() => {
    const seen = new Set();
    return data.filter((item) => {
      const key = `${item.type}_${item.container_size}_${item.etd}_${item.base_freight}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data]);

  const carrierImages = {
    msc: "/imagens/armadores/Msc.png",
    hapag: "/imagens/armadores/Hapag.png",
    hapagspot: "/imagens/armadores/Hapagspot.png",
    one: "/imagens/armadores/One.png",
    cmacgm: "/imagens/armadores/CmaCgm.png",
    maersk: "/imagens/armadores/Maersk.png",
    searates: "/imagens/armadores/Searates.png",
    zim: "/imagens/armadores/Zim.png",
  };

  const filteredData = useMemo(() => {
    const filterData = uniqueData.filter((item) => {
      if (item.waiting_bot) return true;

      if (!item.values || item.values.length === 0) return false;

      const containerMatch =
        containerFilter.length === 0 ||
        containerFilter.includes(item.container_size);

      const carrierMatch =
        carrierFilter.length === 0 || carrierFilter.includes(item.type);

      const total = calculateTotal(item);
      const priceMatch = total >= priceRange[0] && total <= priceRange[1];

      const passes = containerMatch && carrierMatch && priceMatch;

      return passes;
    });

    return filterData.sort((a, b) => {
      if (a.waiting_bot && !b.waiting_bot) return 1;
      if (!a.waiting_bot && b.waiting_bot) return -1;
      return calculateTotal(a) - calculateTotal(b);
    });
  }, [uniqueData, containerFilter, carrierFilter, priceRange]);

  const filterOptions = useMemo(() => {
    const containers = Array.from(
      new Set(uniqueData.map((item) => item.container_size))
    ).filter(Boolean);
    const carriers = Array.from(
      new Set(uniqueData.map((item) => item.type?.toLowerCase()))
    ).filter(Boolean);
    const prices = uniqueData.map(calculateTotal).filter((price) => price > 0);
    const maxPrice = prices.length > 0 ? Math.max(...prices) : 100000;

    return {
      containers: containers.map((size) => ({
        label: `${size}'`,
        value: size,
      })),
      carriers: carriers.map((carrier) => ({
        label: carrier.toUpperCase(),
        value: carrier,
      })),
      maxPrice: maxPrice,
    };
  }, [uniqueData]);

  React.useEffect(() => {
    setPriceRange([0, filterOptions.maxPrice]);
  }, [filterOptions.maxPrice]);

  const handleViewTaxes = (record) => {
    setSelectedTaxes(record.values || []);
    setSelectedRecord(record || []);
    setIsTaxModalVisible(true);
  };

  const handleViewDD = (record) => {
    const ddData = {
      export:
        record.sailing_info.otherInfos?.detentionDemurrage
          ?.ExportDetentionDemurrageDetails || [],
      import:
        record.sailing_info.otherInfos?.detentionDemurrage
          ?.ImportDetentionDemurrageDetails || [],
    };
    setSelectedDDData(ddData);
    setIsDDModalVisible(true);
  };

  const taxColumns = [
    {
      title: "Descrição",
      dataIndex: "category",
      key: "description",
    },
    {
      title: "Valor",
      dataIndex: "value",
      key: "value",
      render: (value, record) => `${value} ${record.currency}`,
    },
  ];

  const columns = [
    {
      title: <span style={{ fontSize: "11px" }}>Carrier</span>,
      dataIndex: "type",
      sorter: (a, b) => (a.type || "").localeCompare(b.type || ""),
      render: (value, record) => {
        if (!value && !record.waiting_bot) {
          return <LoadingSkeleton />;
        }

        const imageUrl = carrierImages[value?.toLowerCase()];
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            {record.import && !record?.imported_by && (
              <InfoCircleOutlined
                style={{
                  color: "#1890ff",
                  fontSize: "20px",
                  marginRight: "10px",
                }}
                title="Registro importado"
              />
            )}
            {imageUrl && (
              <img
                src={imageUrl}
                alt={value}
                style={{ width: 25, height: 18, objectFit: "contain" }}
              />
            )}
            <span style={{ fontSize: "11px" }}>{value?.toUpperCase()}</span>
            {record.import && record?.imported_by && (
              <span style={{ fontSize: "11px", marginLeft: '4px' }}>{record?.imported_by}</span>
            )}
          </div>
        );
      },
    },

    {
      title: <span style={{ fontSize: "11px" }}>Container</span>,
      dataIndex: "container_size",
      sorter: (a, b) =>
        Number(a.container_size || 0) - Number(b.container_size || 0),
      render: (size, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton />;
        }
        return (
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px" }}>
              {Number(size) == 20 ? "20'DV" : "40'DV/HC"}
            </span>
          </div>
        );
      },
    },

    {
      title: <span style={{ fontSize: "11px" }}>Freight All-in</span>,
      key: "value",
      sorter: (a, b) => calculateTotal(a) - calculateTotal(b),
      defaultSortOrder: "ascend", // ← força ordem inicial crescente na UI
      render: (_, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Calculando..." />;
        }

        const total = sumTaxes(record.values, record).freigth;
        const content = (
          <div style={{ fontSize: "11px" }}>
            <div>Base: {record.base_freight || 0} USD</div>
            <div>Bunker: {record.bunker || 0} USD</div>
            <div>ISPS: {record.isps || 0} USD</div>
          </div>
        );

        const color =
          total > 5000 ? "#DC2626" : total > 3000 ? "#D97706" : "#059669";

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Badge
              count={`USD ${total.toLocaleString("en-US")} `}
              showZero={true}
              overflowCount={9999}
              style={{
                backgroundColor: color,
                color: "white",
                fontSize: "11px",
                padding: "0 2px",
              }}
            />
          </div>
        );
      },
    },

    {
      title: <span style={{ fontSize: "11px" }}>Details</span>,
      key: "Details",
      render: (_, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Carregando..." />;
        }
        return (
          <div style={{ textAlign: "center" }}>
            {record.values && record.values.length > 0 && (
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewTaxes(record)}
                style={{ fontSize: "11px", padding: "0 4px" }}
              >
                Rate Details
              </Button>
            )}
          </div>
        );
      },
    },

    {
      title: <span style={{ fontSize: "11px" }}>Vessel</span>,
      dataIndex: "vesselName",
      render: (_, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Buscando navio..." />;
        }

        const vesselName = record.sailing_info?.vesselName || "N/A";
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            {/* <Ship size={12} /> */}
            <span
              style={{
                fontSize: "11px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                maxWidth: "120px",
              }}
            >
              {vesselName}
            </span>
          </div>
        );
      },
    },

    {
      title: <span style={{ fontSize: "11px" }}>Transhipment</span>,
      key: "transhipment",
      render: (_, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Carregando..." />;
        }

        return (
          <div style={{ textAlign: "center", fontSize: "11px" }}>{record?.transhipment || 'Direct'}</div>
        );
      },
    },

    {
      title: <span style={{ fontSize: "11px" }}>ETA Origin
      </span>,
      dataIndex: "etd",
      sorter: (a, b) => dayjs(a.eta).unix() - dayjs(b.eta).unix(),
      render: (date, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Buscando data..." />;
        }
        if (record.import) return <div style={{ textAlign: "center" }}>--</div>;
        if (!date) return <div style={{ textAlign: "center" }}>-</div>;
        if (record.type?.toLowerCase() === "msc" && record.etd === record.eta)
          return <div style={{ textAlign: "center" }}>TBA</div>;
        return (
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px" }}>
              {dayjs(date).format("DD MMM YYYY")}
            </span>
          </div>
        );
      },
    },

    {
      title: <span style={{ fontSize: "11px" }}>ETA Destination
      </span>,
      dataIndex: "eta",
      sorter: (a, b) => dayjs(a.etd).unix() - dayjs(b.etd).unix(),
      render: (date, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Buscando data..." />;
        }
        if (record.import) return <div style={{ textAlign: "center" }}>--</div>;
        if (!date) return <div style={{ textAlign: "center" }}>-</div>;
        if (record.type?.toLowerCase() === "msc" && record.etd === record.eta)
          return <div style={{ textAlign: "center" }}>TBA</div>;
        return (
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px" }}>
              {dayjs(date).format("DD MMM YYYY")}
            </span>
          </div>
        );
      },
    },



    {
      title: <span style={{ fontSize: "11px" }}>T/T</span>,
      dataIndex: "transit_time",
      sorter: (a, b) =>
        Number(a.transit_time || 0) - Number(b.transit_time || 0),
      render: (time, record) => {
        return (
          <div style={{ textAlign: "center" }}>
            {record.waiting_bot ? (
              <LoadingSkeleton text="Calculando..." />
            ) : (
              <span style={{ fontSize: "11px" }}>{record.transit_time}</span>
            )}
          </div>
        );
      },
    },

    {
      title: <span style={{ fontSize: "11px" }}>Validity</span>,
      dataIndex: "offerExpire",
      render: (_, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Verificando..." />;
        }

        if (record.import) {
          return (
            <div style={{ textAlign: "center" }}>
              <span style={{ fontSize: "11px" }}>
                {record.eta ? dayjs(record.eta).format("DD MMM YYYY") : "N/A"}
              </span>
            </div>
          );
        }

        const expireDate = record.sailing_info?.offerExpire;
        return (
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px" }}>
              {expireDate ? dayjs(expireDate).format("DD MMM YYYY") : "N/A"}
            </span>
          </div>
        );
      },
    },

    {
      title: (
        <span style={{ fontSize: "11px" }}>
          Freetime
          <br />
          Origin DET
        </span>
      ),
      key: "DEM",
      render: (_, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Calculando..." />;
        }

        return (
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px" }}>
              {record?.freetime?.export?.det || ''}
            </span>
          </div>
        );
      },
    },

    {
      title: (
        <span style={{ fontSize: "11px" }}>
          Freetime
          <br />
          Destination DEM
        </span>
      ),
      key: "DET",
      render: (_, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Calculando..." />;
        }

        return (
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px" }}>
              {record?.freetime?.import?.dem || ''}
            </span>
          </div>
        );
      },
    },


    {
      title: <span style={{ fontSize: "11px" }}>Actions</span>,
      key: "action",
      width: "200px",
      render: (_, record) => {
        if (record.waiting_bot) {
          return <LoadingSkeleton text="Preparando..." />;
        }

        if (record.import) {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                minWidth: "180px",
                whiteSpace: "nowrap",
              }}
            >
              <Button
                type="primary"
                size="small"
                icon={<SendOutlined />}
                onClick={() => handleProposal(record)}
                style={{
                  fontSize: "11px",
                  padding: "0 6px",
                  height: "24px",
                  width: "90px",
                  minWidth: "90px",
                }}
              >
                Proposal
              </Button>
            </div>
          );
        }

        const unavailable =
          record.from === "TBI" ||
          record.transit_time === "TBA" ||
          record.base_freight === "No space available";

        const carrier = record.type?.toLowerCase();
        const portFromCode = record.from_code || "BRSSZ";
        const portFromName = record.from || "SANTOS";
        const portToCode = record.to_code || "DEHAM";
        const portToName = record.to || "hamburg";
        const etd = record.etd ? dayjs(record.etd).format("YYYY-MM-DD") : "";

        let scheduleUrl = null;

        switch (carrier) {
          case "hapag":
            scheduleUrl = `https://www.hapag-lloyd.com/solutions/schedule/#/?sl=${portFromCode}&sn=${portFromName}&el=${portToCode}&en=${portToName}&exportHaulage=MH&importHaulage=MH&containerType=45GP&departureDate=${etd}&usFlag=false&dg=true&reefer`;
            break;
          case "hapagspot":
            scheduleUrl = `https://www.hapag-lloyd.com/solutions/schedule/#/?sl=${portFromCode}&sn=${portFromName}&el=${portToCode}&en=${portToName}&exportHaulage=MH&importHaulage=MH&containerType=45GP&departureDate=${etd}&usFlag=false&dg=true&reefer`;
            break;
          case "msc":
            scheduleUrl = "https://www.msc.com/pt/search-a-schedule";
            break;
          case "cmacgm":
            scheduleUrl = "https://www.cma-cgm.com/ebusiness/schedules";
            break;
          case "maersk":
            const originId =
              record?.sailing_info?.otherInfos?.maerskGeoLocationIdOrigin;
            const destinationId =
              record?.sailing_info?.otherInfos?.maerskGeoLocationIdDestination;

            if (originId && destinationId) {
              const momentDate = moment(record.etd).format("YYYY-MM-DD");
              const containerIsoCode =
                record.container_size === "40" ? "45G1" : "22G1";
              scheduleUrl = `https://www.maersk.com/schedules/pointToPoint?from=${originId}&to=${destinationId}&containerIsoCode=${containerIsoCode}&fromServiceMode=CY&toServiceMode=CY&numberOfWeeks=4&dateType=D&date=${momentDate}&dateTo=&vesselFlag=`;
            } else {
              scheduleUrl = "https://www.maersk.com/schedules/pointToPoint";
            }
            break;
          case "one":
            scheduleUrl =
              "https://ecomm.one-line.com/one-ecom/schedule/point-to-point-schedule?advanceSearch=true";
            break;
          case "zim":
            scheduleUrl = "https://www.zim.com/schedules/point-to-point";
            break;
          default:
            return <div style={{ textAlign: "center" }}>N/A</div>;
        }

        if (unavailable) return <div style={{ textAlign: "center" }}>-</div>;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 4,
              minWidth: "180px",
              whiteSpace: "nowrap",
            }}
          >
            {carrier === "one" || carrier === "cmacgm" || carrier === "msc" ? (
              <Button
                size="small"
                type="primary"
                icon={<ScheduleOutlined />}
                onClick={() =>
                  openModal({
                    originCode: record.from_code,
                    destinationCode: record.to_code,
                    startDate: moment(record.etd)
                      .startOf("month")
                      .format("YYYY-MM-DD"),
                    endDate: record.eta,
                    company: carrier.toLowerCase(),
                  })
                }
                style={{
                  fontSize: "11px",
                  padding: "0 6px",
                  width: "90px",
                  height: "24px",
                }}
              >
                Schedule
              </Button>
            ) : (
              <a href={scheduleUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  size="small"
                  type="primary"
                  icon={<ScheduleOutlined />}
                  style={{
                    fontSize: "11px",
                    padding: "0 6px",
                    height: "24px",
                    width: "90px",
                  }}
                >
                  Schedule
                </Button>
              </a>
            )}
            <Button
              type="primary"
              size="small"
              icon={<ShoppingCartOutlined />}
              onClick={() => handleBooking(record)}
              style={{
                fontSize: "11px",
                padding: "0 6px",
                height: "24px",
                width: "60px",
                minWidth: "60px",
              }}
            >
              Booking
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleProposal(record)}
              style={{
                fontSize: "11px",
                padding: "0 6px",
                height: "24px",
                width: "90px",
                minWidth: "90px",
              }}
            >
              Proposal
            </Button>
          </div>
        );
      },
    },
  ];

  const generateQuotationNo = (record) => {
    const containerSize = record.container_size || "";
    const etd = record.etd ? dayjs(record.etd).format("YYYY-MM-DD") : "";
    const transitDays =
      parseInt((record.transit_time || "0").replace(/\D/g, "")) || 0; // Extrai número de "35 DAYS"
    const eta = etd
      ? dayjs(etd).add(transitDays, "days").format("YYYY-MM-DD")
      : "";
    const from = record.from || "";
    const fromCode = record.from_code || from.split(" ")[0] || ""; // Extrai código se não fornecido
    // Mescla: container_size-eta-etd-from-from_code
    const merged =
      `${containerSize}-${eta}-${etd}-${from}-${fromCode}`.toUpperCase();
    // Hash simples (djb2) para gerar código único
    let hash = 0;
    for (let i = 0; i < merged.length; i++) {
      hash = (hash << 5) - hash + merged.charCodeAt(i);
      hash |= 0; // Converte para inteiro 32bit
    }
    const hashString = Math.abs(hash)
      .toString(36)
      .toUpperCase()
      .padStart(6, "0"); // Alfanumérico curto
    return `WLI-${hashString}`; // Ex: "WLI-ABC123"
  };

  const handleProposal = (record) => {
    // Transformar as taxas em formato agrupado antes de navegar
    const groupedTaxes = (record.values || []).reduce((acc, tax) => {
      const category = categorizeTax(tax.category, tax.currency, record, tax);
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(tax);
      return acc;
    }, {});

    setFees(groupedTaxes); // salvar na store

    const { setProposalData } = useProposalStore.getState();

    record = record;
    if (record.type === "hapagspot") {
      record.type = "hapag";
    }
    const etd = record.etd || null;
    const transitDays =
      parseInt((record.transit_time || "").replace(/\D/g, "")) || 0;

    const eta =
      etd && transitDays
        ? dayjs(etd).add(transitDays, "day").format("YYYY-MM-DD")
        : record.eta || null;

    const proposalData = {
      quotationNo: generateQuotationNo(record),

      from: `${record.from_code || ""} ${record.from || ""}`.trim(),
      to: `${record.to_code || ""} ${record.to || ""}`.trim(),

      mode: "SEA-FCL",
      date: dayjs().format("YYYY-MM-DD"),
      expiryDate: dayjs().add(30, "days").format("YYYY-MM-DD"),

      containerType: record.container_size
        ? `${record.container_size}GP`
        : "",

      grossWeight: 20000,
      incoTerms: "",
      carrier: record.type?.toUpperCase() || "",

      route: `${record.from || ""} - ${record.to || ""}`,
      cargoType: "GENERAL CARGO",
      commodity: "GENERAL CARGO",
      serviceType: "CY/CY",
      eta,
      etd,
      etaOrigin: eta,
      etdDestination: etd,
      transhipment: record.transhipment || "Direct",
      transitTime: record.transit_time,
      freeDays: 5,
      terms: "",
    };

    setProposalData(proposalData);

    navigate(`/proposal`);
  };

  const handleBooking = (record) => {
    const { setGroupedTaxes } = useTaxStore.getState();

    // Transformar as taxas em formato agrupado antes de navegar
    const groupedTaxes = (record.values || []).reduce((acc, tax) => {
      const category = categorizeTax(tax.category, tax.currency, record, tax);
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(tax);
      return acc;
    }, {});


    setGroupedTaxes(groupedTaxes); // salvar na store

    // resto do código igual
    let type = record.type;
    if (record.type === "searates") {
      type = "maersk";
    }
    if (record.type === "hapagspot") {
      type = "hapag";
    }

    const params = new URLSearchParams({
      armador: type || "",
      tipo_container: record.container_size || "",
      porto_embarque: record.from || "",
      porto_descarga: record.to || "",
      navio: record.vessel || "",
      data_embarque: record.etd || "",
      data_chegada: record.eta || "",
      tempo_de_transito: record.transit_time || "",
      frete: calculateTotal(record).toString(),
      base: record.base_freight?.toString() || "",
      bunker: record.bunker?.toString() || "",
      isps: record.isps?.toString() || "",
    });

    navigate(`/booking?${params.toString()}`);
  };

  const sumTaxes = (taxes, record) => {
    const INVALID = Symbol("invalid");
    const UNKNOWN_GROUP = "unknown";

    // Normaliza a entrada
    const list = Array.isArray(taxes) ? taxes : [];
    const skipped = []; // opcional: para debug

    const safeParseValue = (v) => {
      try {
        const n = parseTaxValue ? parseTaxValue(v) : Number(v);
        return Number.isFinite(n) ? n : INVALID;
      } catch {
        return INVALID;
      }
    };

    const safeCategory = (tax) => {
      try {
        const cat = categorizeTax
          ? categorizeTax(tax?.category, tax?.currency, record, tax)
          : tax?.category;
        return cat || UNKNOWN_GROUP;
      } catch {
        return UNKNOWN_GROUP;
      }
    };

    const groupedTaxes = list.reduce((acc, tax, idx) => {
      // pula itens claramente inválidos
      if (!tax || typeof tax !== "object") {
        skipped.push({ idx, reason: "tax undefined/null ou não-objeto" });
        return acc;
      }

      // valida valor
      const valueNum = safeParseValue(tax.value);
      if (valueNum === INVALID) {
        skipped.push({ idx, reason: "valor inválido", value: tax.value });
        return acc;
      }

      // resolve categoria de forma segura
      const category = safeCategory(tax);

      if (!acc[category]) acc[category] = [];
      // opcional: já guarda o valor numérico normalizado para evitar reparse
      acc[category].push({ ...tax, _valueNum: valueNum });
      return acc;
    }, {});

    // soma segura usando o valor já normalizado (_valueNum) como preferência
    const sumGroup = (group) =>
      (group || []).reduce((sum, tax) => {
        const v =
          typeof tax?._valueNum === "number"
            ? tax._valueNum
            : safeParseValue(tax?.value);
        return v === INVALID ? sum : sum + v;
      }, 0);

    const res = {
      origin: sumGroup(groupedTaxes["Taxas locais da origem"]),
      freigth: sumGroup(groupedTaxes["Frete e taxas que acompanham frete"]), // mantém sua chave
      destination: sumGroup(groupedTaxes["Taxas locais do destino"]),
    };

    // opcional: se quiser ver o que foi ignorado
    // console.debug('Taxes ignorados:', skipped);

    return res;
  };

  const renderTaxesModalContent = () => {
    const groupedTaxes = selectedTaxes.reduce((acc, tax) => {
      const category = categorizeTax(
        tax.category,
        tax.currency,
        selectedRecord,
        tax
      );
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(tax);
      return acc;
    }, {});

    // Ordem específica para os painéis
    const panelOrder = [
      "Taxas locais da origem",
      "Frete e taxas que acompanham frete",
      "Taxas locais do destino",
      "Outras taxas",
    ];

    return (
      <Collapse defaultActiveKey={panelOrder}>
        {panelOrder.map(
          (category) =>
            groupedTaxes[category] && (
              <Panel header={category} key={category}>
                <Table
                  dataSource={groupedTaxes[category]}
                  columns={[
                    {
                      title: "Descrição",
                      dataIndex: "category",
                      key: "description",
                      render: (value, record) =>
                        getTaxDescription(record, selectedRecord),
                    },
                    {
                      title: "Valor",
                      dataIndex: "value",
                      key: "value",
                      render: (value, record: any) =>
                        `${record.value} ${record.currency}`,
                    },
                  ]}
                  scroll={{ x: "max-content" }}
                  rowKey={(record: any, index) => `${record.category}-${index}`}
                  pagination={false}
                  size="small"
                />
              </Panel>
            )
        )}
      </Collapse>
    );
  };

  return (
    <>
      <Card
        title={
          <div className="flex items-center gap-3">
            <Button
              icon={<FilterOutlined />}
              onClick={() => setIsFilterVisible(!isFilterVisible)}
              type="text"
            >
              {isFilterVisible ? "Ocultar filtros" : "Mostrar filtros"}
            </Button>
            <Badge
              count={filteredData.length}
              style={{ backgroundColor: "#52c41a" }}
            />
            {uniqueData.length > 0 && (
              <span className="text-gray-500">
                (Total: {uniqueData.length})
              </span>
            )}
          </div>
        }
        className="mb-4 mt-4"
      >
        {isFilterVisible && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Form.Item label="Tamanho do Container">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Selecione os tamanhos"
                  value={containerFilter}
                  onChange={setContainerFilter}
                  options={filterOptions.containers}
                />
              </Form.Item>
              <Form.Item label="Carrier">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Selecione os armadores"
                  value={carrierFilter}
                  onChange={setCarrierFilter}
                  options={filterOptions.carriers}
                />
              </Form.Item>
            </div>
            <Form.Item label="Faixa de Valor (USD)">
              <Slider
                range
                min={0}
                max={filterOptions.maxPrice}
                value={priceRange}
                onChange={setPriceRange}
                tooltip={{
                  formatter: (value) => `$${value?.toLocaleString()}`,
                }}
              />
            </Form.Item>
          </div>
        )}
      </Card>

      <div ref={ref}>
        <Card
          title="Lista de Fretes"
          extra={
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportToExcel(filteredData)}
                type="primary"
                ghost
              >
                Exportar Excel
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportToPDF(filteredData)}
                type="primary"
                ghost
              >
                Exportar PDF
              </Button>
            </Space>
          }
        >
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey={(record) =>
              `${record.user_id}_${record.etd}_${record.type}_${Math.random()}`
            }
            pagination={{
              pageSize: 200,
              showTotal: (total) => `Total: ${total} registros`,
            }}
            scroll={{
              x: 1200, // Define largura mínima específica
              y: 600, // Define altura máxima para scroll vertical
            }}
            loading={loading}
            size="small" // Reduz o padding das células
            locale={{
              emptyText: <Empty description="Sem registros" />,
              triggerDesc: "Ordenar descendente",
              triggerAsc: "Ordenar ascendente",
              cancelSort: "Cancelar ordenação",
            }}
            style={{
              width: "100%",
              overflow: "hidden",
            }}
          />
        </Card>
      </div>

      <ScheduleModal
        isOpen={isOpen}
        data={dataModal}
        loading={loadingModal}
        error={error}
        closeModal={closeModal}
        params={params}
      />

      <Modal
        title="Frete e Taxas"
        visible={isTaxModalVisible}
        onCancel={() => setIsTaxModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsTaxModalVisible(false)}>
            Fechar
          </Button>,
        ]}
        width={800}
      >
        {renderTaxesModalContent()}
      </Modal>
    </>
  );
});

export default FreightList;